import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { eventEmitter, EVENTS } from '@/lib/events';
import {
  Workflow,
  Users,
  BarChart3,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Key,
  Variable,
  PlayCircle,
  Table2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { foldersApi, workflowsApi, teamsApi, teamFoldersApi } from '@/lib/api';
import { toast } from 'sonner';
import {
  CreateTeamFolderDialog,
  RenameTeamFolderDialog,
  DeleteTeamFolderDialog,
} from '@/components/dialogs/TeamFolderDialogs';
import { Textarea } from '@/components/ui/textarea';
import { DeleteFolderDialog } from '@/components/workflow/DeleteFolderDialog';
import { DroppableFolderItem } from '@/components/workflow/DroppableFolderItem';
import { DroppableRootFolder } from '@/components/workflow/DroppableRootFolder';
import { DroppableTeamRootFolder } from '@/components/workflow/DroppableTeamRootFolder';
import { DroppableTeamFolderItem } from '@/components/workflow/DroppableTeamFolderItem';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { useSubscription } from '@/contexts/SubscriptionContext';

interface Folder {
  id: string;
  name: string;
  parentId: string | null;
  workflowCount?: number;
}

interface Team {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
}

interface TeamFolder {
  id: string;
  name: string;
  teamId: string;
  parentId: string | null;
  isRoot: boolean;
  workflowCount?: number;
  subFolders?: TeamFolder[];
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkFlag } = useFeatureToggle();
  const { subscriptionId, loading: subscriptionLoading } = useSubscription();
  const exampleFlagEnabled = checkFlag('ExampleFlag', false, { userId: '' });
  const [workflowsExpanded, setWorkflowsExpanded] = useState(true);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [rootWorkflowCount, setRootWorkflowCount] = useState<number>(0);

  // Section expansion state (for collapsible sections)
  const [myWorkflowsExpanded, setMyWorkflowsExpanded] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<Record<string, boolean>>({});

  // Team workflows state
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamFolders, setTeamFolders] = useState<Record<string, TeamFolder[]>>({});
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamRefreshTrigger, setTeamRefreshTrigger] = useState(0);

  // Folder management state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderId, setRenameFolderId] = useState<string | null>(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [deleteFolderId, setDeleteFolderId] = useState<string | null>(null);
  const [deleteFolderName, setDeleteFolderName] = useState('');
  const [deleteWorkflowCount, setDeleteWorkflowCount] = useState(0);
  const [deleteSubfolderCount, setDeleteSubfolderCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create dialog state
  const [createItemDialogOpen, setCreateItemDialogOpen] = useState(false);


  const [createType, setCreateType] = useState<'workflow' | 'project' | 'team' | null>(null);
  const [newItem, setNewItem] = useState({ name: '', description: '' });

  // Team folder dialog state
  const [createTeamFolderOpen, setCreateTeamFolderOpen] = useState(false);
  const [renameTeamFolderOpen, setRenameTeamFolderOpen] = useState(false);
  const [deleteTeamFolderOpen, setDeleteTeamFolderOpen] = useState(false);
  const [selectedTeamFolder, setSelectedTeamFolder] = useState<{
    teamId: string;
    teamName: string;
    folderId?: string;
    folderName?: string;
    workflowCount?: number;
  } | null>(null);

  // Load section expansion state from localStorage on mount
  useEffect(() => {
    const savedMyWorkflows = localStorage.getItem('sidebar-myWorkflows-expanded');
    const savedTeams = localStorage.getItem('sidebar-teams-expanded');

    if (savedMyWorkflows !== null) {
      setMyWorkflowsExpanded(savedMyWorkflows === 'true');
    }
    if (savedTeams) {
      try {
        setExpandedTeams(JSON.parse(savedTeams));
      } catch {
        // Ignore invalid JSON
      }
    }
  }, []);

  // Save myWorkflowsExpanded to localStorage when changed
  useEffect(() => {
    localStorage.setItem('sidebar-myWorkflows-expanded', String(myWorkflowsExpanded));
  }, [myWorkflowsExpanded]);

  // Save expandedTeams to localStorage when changed
  useEffect(() => {
    localStorage.setItem('sidebar-teams-expanded', JSON.stringify(expandedTeams));
  }, [expandedTeams]);

  // Helper functions for team expansion
  const toggleTeamExpanded = (teamId: string) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: prev[teamId] === undefined ? false : !prev[teamId]
    }));
  };

  const isTeamExpanded = (teamId: string) => {
    return expandedTeams[teamId] !== false; // Default to expanded
  };

  useEffect(() => {
    // Wait for subscription context to be ready before making API calls
    if (subscriptionLoading || !subscriptionId) {
      return;
    }
    if (workflowsExpanded && !collapsed) {
      loadFolders();
      loadTeams();
    }
  }, [workflowsExpanded, collapsed, teamRefreshTrigger, subscriptionId, subscriptionLoading]);

  // Refresh teams when navigating to Teams or Workflows page
  // This ensures invited members see updated teams when they navigate
  useEffect(() => {
    // Wait for subscription context to be ready before making API calls
    if (subscriptionLoading || !subscriptionId) {
      return;
    }
    if (location.pathname === '/teams' || location.pathname.startsWith('/workflows')) {
      if (!collapsed) {
        loadTeams();
      }
    }
  }, [location.pathname, collapsed, subscriptionId, subscriptionLoading]);

  // Refresh teams when page becomes visible (user returns to tab)
  // This ensures invited members see teams when they switch back to the app
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only refresh if subscription context is ready
      if (!document.hidden && !collapsed && subscriptionId && !subscriptionLoading) {
        loadTeams();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [collapsed, subscriptionId, subscriptionLoading]);

  // Listen for team updates - Always load teams regardless of sidebar state
  useEffect(() => {
    const handleTeamsUpdated = () => {
      setTeamRefreshTrigger(prev => prev + 1);
    };

    const handleTeamCreated = () => {
      setTeamRefreshTrigger(prev => prev + 1);
      // Retry after delay to ensure backend has committed changes
      setTimeout(() => {
        setTeamRefreshTrigger(prev => prev + 1);
      }, 1000);
    };

    const handleTeamDeleted = () => {
      setTeamRefreshTrigger(prev => prev + 1);
    };

    const handleTeamUpdated = (data: any) => {
      if (data?.teamId) {
        loadTeamFolders(data.teamId);
      }
      setTeamRefreshTrigger(prev => prev + 1);
    };

    const handleFoldersUpdated = () => {
      setTimeout(() => {
        loadFolders();
      }, 1000);
    };

    const handleTeamFoldersUpdated = (data?: { teamId?: string }) => {
      setTimeout(() => {
        if (data?.teamId) {
          loadTeamFolders(data.teamId);
        } else {
          loadTeams();
        }
      }, 1000);
    };

    eventEmitter.on(EVENTS.TEAMS_UPDATED, handleTeamsUpdated);
    eventEmitter.on(EVENTS.TEAM_CREATED, handleTeamCreated);
    eventEmitter.on(EVENTS.TEAM_DELETED, handleTeamDeleted);
    eventEmitter.on(EVENTS.TEAM_UPDATED, handleTeamUpdated);
    eventEmitter.on(EVENTS.FOLDERS_UPDATED, handleFoldersUpdated);
    eventEmitter.on(EVENTS.TEAM_FOLDERS_UPDATED, handleTeamFoldersUpdated);

    return () => {
      eventEmitter.off(EVENTS.TEAMS_UPDATED, handleTeamsUpdated);
      eventEmitter.off(EVENTS.TEAM_CREATED, handleTeamCreated);
      eventEmitter.off(EVENTS.TEAM_DELETED, handleTeamDeleted);
      eventEmitter.off(EVENTS.TEAM_UPDATED, handleTeamUpdated);
      eventEmitter.off(EVENTS.FOLDERS_UPDATED, handleFoldersUpdated);
      eventEmitter.off(EVENTS.TEAM_FOLDERS_UPDATED, handleTeamFoldersUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Keep empty array but ensure handlers don't reference stale state

  const loadFolders = async () => {
    try {
      setLoadingFolders(true);
      const response = await foldersApi.list();
      // Handle different response structures
      if (response.data) {
        if (Array.isArray(response.data)) {
          setFolders(response.data);
        } else if (response.data.folders && Array.isArray(response.data.folders)) {
          setFolders(response.data.folders);
        } else {
          setFolders([]);
        }
      } else {
        setFolders([]);
      }

      // Load root workflow count (workflows with no folder — backend filters by folderId omission)
      try {
        const workflowsResponse = await workflowsApi.list({ limit: 0 });
        const respData = workflowsResponse.data;
        // New paginated shape: { items, totalCount }, fallback to legacy shapes
        const workflows = Array.isArray(respData?.items)
          ? respData.items
          : (Array.isArray(respData) ? respData : []);
        const rootWorkflows = workflows.filter((w: any) => !w.folderId && !w.teamFolderId);
        // If backend returned totalCount directly (no folder = root), use that; else count client-side
        setRootWorkflowCount(rootWorkflows.length);
      } catch (error) {
        console.error('Failed to load root workflow count:', error);
        setRootWorkflowCount(0);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await teamsApi.list();
      const teamsData = Array.isArray(response.data) ? response.data : [];

      // Sort by created date descending (backend should do this, but ensure it here too)
      const sortedTeams = teamsData.sort((a: Team, b: Team) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setTeams(sortedTeams);

      // Load folders for all teams
      for (const team of sortedTeams) {
        loadTeamFolders(team.id);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
      setTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadTeamFolders = async (teamId: string) => {
    try {
      const response = await teamFoldersApi.getStructure(teamId);
      const structure = Array.isArray(response.data) ? response.data : [];
      setTeamFolders(prev => ({ ...prev, [teamId]: structure }));
    } catch (error) {
      console.error(`Failed to load folders for team ${teamId}:`, error);
      setTeamFolders(prev => ({ ...prev, [teamId]: [] }));
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const response = await foldersApi.create({ name: newFolderName, parentId: null });
      toast.success('Folder created successfully');
      setNewFolderName('');
      setCreateDialogOpen(false);
      loadFolders();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderName.trim() || !renameFolderId) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      await foldersApi.update(renameFolderId, { name: renameFolderName });
      toast.success('Folder renamed successfully');
      setRenameFolderName('');
      setRenameFolderId(null);
      setRenameDialogOpen(false);
      loadFolders();
    } catch (error) {
      toast.error('Failed to rename folder');
    }
  };

  const openDeleteDialog = async (folderId: string, folderName: string) => {
    try {
      // Fetch workflow and subfolder counts
      const response = await foldersApi.getWorkflowCount(folderId);
      setDeleteFolderId(folderId);
      setDeleteFolderName(folderName);
      setDeleteWorkflowCount(response.data.workflowCount);
      setDeleteSubfolderCount(response.data.subfolderCount);
      setDeleteDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load folder information');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteFolderId) return;

    setIsDeleting(true);
    try {
      const hadWorkflows = deleteWorkflowCount > 0;
      await foldersApi.delete(deleteFolderId);

      if (hadWorkflows) {
        toast.success(`Folder deleted successfully. ${deleteWorkflowCount} workflow${deleteWorkflowCount !== 1 ? 's' : ''} moved to root folder.`);
      } else {
        toast.success('Folder deleted successfully');
      }

      setDeleteDialogOpen(false);
      loadFolders();

      // If workflows were moved to root, navigate to root and force refresh
      if (hadWorkflows) {
        // Add timestamp to force page reload
        navigate(`/workflows?refresh=${Date.now()}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to delete folder');
    } finally {
      setIsDeleting(false);
    }
  };

  const openRenameDialog = (folderId: string, folderName: string) => {
    setRenameFolderId(folderId);
    setRenameFolderName(folderName);
    setRenameDialogOpen(true);
  };

  const openCreateDialog = (type: 'workflow' | 'project' | 'team') => {
    setCreateType(type);
    setCreateItemDialogOpen(true);
  };

  const handleCreateItem = async () => {
    if (!newItem.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      if (createType === 'workflow') {
        const response = await workflowsApi.create({
          name: newItem.name,
          description: newItem.description,
          nodes: [],
          edges: [],
          variables: {},
        });
        toast.success('Workflow created successfully');

        // Emit events to update sidebar counts
        eventEmitter.emit(EVENTS.FOLDERS_UPDATED);
        eventEmitter.emit(EVENTS.TEAM_FOLDERS_UPDATED);

        navigate(`/workflows/${response.data.id}`);
      } else if (createType === 'project') {
        // TODO: Implement project creation
        toast.success('Project created successfully');
        navigate('/projects');
      } else if (createType === 'team') {
        // TODO: Implement team creation
        toast.success('Team created successfully');
        navigate('/teams');
      }

      setCreateItemDialogOpen(false);
      setNewItem({ name: '', description: '' });
      setCreateType(null);
    } catch (error) {
      toast.error('Failed to create item');
    }
  };

  const navItems = [
    {
      label: 'Workflows',
      icon: Workflow,
      path: '/workflows',
      expandable: true,
      expanded: workflowsExpanded,
      onToggle: () => setWorkflowsExpanded(!workflowsExpanded),
    },
    {
      label: 'Executions',
      icon: PlayCircle,
      path: '/executions',
    },
    {
      label: 'Variables',
      icon: Variable,
      path: '/variables',
    },
    {
      label: 'Credentials',
      icon: Key,
      path: '/credentials',
    },
    {
      label: 'Knowledge Base',
      icon: BookOpen,
      path: '/knowledge-base',
      // Simple link - no expandable logic
    },
    {
      label: 'Data Tables',
      icon: Table2,
      path: '/data-tables',
    },
    {
      label: 'Teams',
      icon: Users,
      path: '/teams',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
    },
  ];

  const isActive = (path: string) => {
    if (path === '/workflows') {
      return location.pathname === '/workflows' || location.pathname.startsWith('/workflows/');
    }
    if (path === '/knowledge-base') {
      return location.pathname === '/knowledge-base' || location.pathname.startsWith('/knowledge-base');
    }
    if (path === '/data-tables') {
      return location.pathname === '/data-tables' || location.pathname.startsWith('/data-tables');
    }
    return location.pathname === path;
  };

  return (
    <aside
      className={cn(
        'flex flex-col border-r bg-card transition-all duration-300 overflow-hidden flex-shrink-0',
        collapsed ? 'w-16 min-w-16 max-w-16' : 'w-56 min-w-56 max-w-56'
      )}
    >
      {/* Logo/Brand with Toggle */}
      <div className={cn(
        "flex items-center h-14 px-4 border-b",
        collapsed ? "justify-center" : "justify-between"
      )}>
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/e2e-logo.svg"
              alt="E2E Logo"
              className="w-8 h-8"
            />
            <div className="flex flex-col">
              <span className="text-sm font-semibold">Workflow Automation</span>
            </div>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="h-8 w-8 flex-shrink-0"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-2">
        <nav className="space-y-1">
          {/* Create Button */}
          <div className="pb-2 mb-2 border-b">
            {collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="w-full h-10"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => openCreateDialog('workflow')}>
                        <Workflow className="h-4 w-4 mr-2" />
                        Workflow
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/credentials?create=true')}>
                        <Key className="h-4 w-4 mr-2" />
                        Credential
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openCreateDialog('team')}>
                        <Users className="h-4 w-4 mr-2" />
                        Team
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/data-tables?create=true')}>
                        <Table2 className="h-4 w-4 mr-2" />
                        Data Table
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/knowledge-base?create=true')}>
                        <BookOpen className="h-4 w-4 mr-2" />
                        Knowledge Base
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TooltipTrigger>
                <TooltipContent side="right">
                  Create
                </TooltipContent>
              </Tooltip>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    className="w-full justify-start h-10 gap-2"
                  >
                    <Plus className="h-5 w-5" />
                    <span className="flex-1 text-left">Create</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={() => openCreateDialog('workflow')}>
                    <Workflow className="h-4 w-4 mr-2" />
                    Workflow
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/credentials?create=true')}>
                    <Key className="h-4 w-4 mr-2" />
                    Credential
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openCreateDialog('team')}>
                    <Users className="h-4 w-4 mr-2" />
                    Team
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/data-tables?create=true')}>
                    <Table2 className="h-4 w-4 mr-2" />
                    Data Table
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/knowledge-base?create=true')}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    Knowledge Base
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            if (collapsed) {
              return (
                <Tooltip key={item.path} delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={active ? 'secondary' : 'ghost'}
                      size="icon"
                      className={cn(
                        'w-full h-10',
                        active && 'bg-secondary'
                      )}
                      onClick={() => navigate(item.path)}
                    >
                      <Icon className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return (
              <div key={item.path}>
                <Button
                  variant={active ? 'secondary' : 'ghost'}
                  className={cn(
                    'w-full justify-start h-10',
                    active && 'bg-secondary'
                  )}
                  onClick={() => {
                    if (item.expandable && item.onToggle) {
                      item.onToggle();
                    } else {
                      navigate(item.path);
                    }
                  }}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.expandable && (
                    item.expanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )
                  )}
                </Button>

                {/* Workflows Submenu */}
                {item.label === 'Workflows' && item.expandable && item.expanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {/* My Workflows Section - Collapsible */}
                    <DroppableRootFolder
                      onClick={() => navigate('/workflows')}
                      workflowCount={rootWorkflowCount}
                      expanded={myWorkflowsExpanded}
                      onToggleExpand={() => setMyWorkflowsExpanded(!myWorkflowsExpanded)}
                      onAddFolder={() => setCreateDialogOpen(true)}
                      hasSubfolders={folders.length > 0}
                    />

                    {myWorkflowsExpanded && (
                      <>
                        {loadingFolders ? (
                          <div className="px-3 py-2 text-xs text-muted-foreground">
                            Loading folders...
                          </div>
                        ) : folders.length > 0 ? (
                          <>
                            {folders.slice(0, 5).map((folder) => (
                              <DroppableFolderItem
                                key={folder.id}
                                id={folder.id}
                                name={folder.name}
                                workflowCount={folder.workflowCount}
                                onClick={() => navigate(`/workflows?folder=${folder.id}`)}
                                onAddFolder={() => setCreateDialogOpen(true)}
                                onRename={() => openRenameDialog(folder.id, folder.name)}
                                onDelete={() => openDeleteDialog(folder.id, folder.name)}
                              />
                            ))}
                            {folders.length > 5 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start h-8 text-xs text-muted-foreground"
                                onClick={() => navigate('/workflows')}
                              >
                                View all folders...
                              </Button>
                            )}
                          </>
                        ) : null}
                      </>
                    )}

                    {/* Team Workflows Section */}
                    {teams.length > 0 && (
                      <>
                        <Separator className="my-2" />
                        {teams.map((team) => {
                          const folders = teamFolders[team.id] || [];
                          const rootFolder = folders.find(f => f.isRoot);
                          const subFolders = folders.filter(f => !f.isRoot);
                          const teamExpanded = isTeamExpanded(team.id);

                          return (
                            <div key={team.id} className="space-y-1">
                              {/* Team Root Folder - Collapsible */}
                              {rootFolder && (
                                <DroppableTeamRootFolder
                                  key={`${rootFolder.id}-${rootFolder.workflowCount}`}
                                  teamId={team.id}
                                  teamName={team.name}
                                  teamFolderId={rootFolder.id}
                                  workflowCount={rootFolder.workflowCount}
                                  onClick={() => navigate(`/workflows?teamFolder=${rootFolder.id}`)}
                                  expanded={teamExpanded}
                                  onToggleExpand={() => toggleTeamExpanded(team.id)}
                                  onAddFolder={() => {
                                    setSelectedTeamFolder({
                                      teamId: team.id,
                                      teamName: team.name,
                                      folderId: rootFolder.id,
                                    });
                                    setCreateTeamFolderOpen(true);
                                  }}
                                  hasSubfolders={subFolders.length > 0}
                                />
                              )}

                              {teamExpanded && (
                                <>
                                  {/* Sub-folders - no onAddFolder since sub-subfolders not supported */}
                                  {subFolders.length > 0 && subFolders.map((folder) => (
                                    <DroppableTeamFolderItem
                                      key={`${folder.id}-${folder.workflowCount}`}
                                      id={folder.id}
                                      name={folder.name}
                                      teamId={team.id}
                                      workflowCount={folder.workflowCount}
                                      onClick={() => navigate(`/workflows?teamFolder=${folder.id}`)}
                                      onRename={() => {
                                        setSelectedTeamFolder({
                                          teamId: team.id,
                                          teamName: team.name,
                                          folderId: folder.id,
                                          folderName: folder.name,
                                        });
                                        setRenameTeamFolderOpen(true);
                                      }}
                                      onDelete={() => {
                                        setSelectedTeamFolder({
                                          teamId: team.id,
                                          teamName: team.name,
                                          folderId: folder.id,
                                          folderName: folder.name,
                                          workflowCount: folder.workflowCount || 0,
                                        });
                                        setDeleteTeamFolderOpen(true);
                                      }}
                                    />
                                  ))}
                                </>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {exampleFlagEnabled && (
        <div className="px-3 py-2 border-t text-xs text-green-500 font-medium">
          {!collapsed && 'Example Flag Enabled'}
        </div>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for your new folder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                placeholder="My Folder"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
            <DialogDescription>
              Enter a new name for this folder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-folder-name">Folder Name</Label>
              <Input
                id="rename-folder-name"
                placeholder="My Folder"
                value={renameFolderName}
                onChange={(e) => setRenameFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameFolder();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRenameFolder}>Rename Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        folderName={deleteFolderName}
        workflowCount={deleteWorkflowCount}
        subfolderCount={deleteSubfolderCount}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />

      {/* Create Item Dialog */}
      <Dialog open={createItemDialogOpen} onOpenChange={setCreateItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Create New {createType === 'workflow' ? 'Workflow' : createType === 'project' ? 'Project' : 'Team'}
            </DialogTitle>
            <DialogDescription>
              {createType === 'workflow' && 'Create a new workflow to automate your processes'}
              {createType === 'project' && 'Create a new project to organize your workflows'}
              {createType === 'team' && 'Create a new team to collaborate with others'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="item-name">Name</Label>
              <Input
                id="item-name"
                placeholder={`Enter ${createType} name`}
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="item-description">Description (optional)</Label>
              <Textarea
                id="item-description"
                placeholder={`Enter ${createType} description`}
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateItemDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateItem}>
              Create {createType === 'workflow' ? 'Workflow' : createType === 'project' ? 'Project' : 'Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Team Folder Dialogs */}
      {selectedTeamFolder && (
        <>
          <CreateTeamFolderDialog
            open={createTeamFolderOpen}
            onOpenChange={setCreateTeamFolderOpen}
            teamId={selectedTeamFolder.teamId}
            teamName={selectedTeamFolder.teamName}
            parentFolderId={selectedTeamFolder.folderId}
            onSuccess={() => {
              loadTeamFolders(selectedTeamFolder.teamId);
              toast.success('Folder created successfully');
            }}
          />
          <RenameTeamFolderDialog
            open={renameTeamFolderOpen}
            onOpenChange={setRenameTeamFolderOpen}
            folderId={selectedTeamFolder.folderId!}
            currentName={selectedTeamFolder.folderName || ''}
            onSuccess={() => {
              // Add delay to allow backend to commit changes
              setTimeout(() => {
                loadTeamFolders(selectedTeamFolder.teamId);
              }, 1000);
            }}
          />
          <DeleteTeamFolderDialog
            open={deleteTeamFolderOpen}
            onOpenChange={setDeleteTeamFolderOpen}
            folderId={selectedTeamFolder.folderId!}
            folderName={selectedTeamFolder.folderName || ''}
            workflowCount={selectedTeamFolder.workflowCount || 0}
            onSuccess={() => {
              loadTeamFolders(selectedTeamFolder.teamId);
              toast.success('Folder deleted successfully');

              // Navigate to the team root folder
              const folders = teamFolders[selectedTeamFolder.teamId] || [];
              const rootFolder = folders.find(f => f.isRoot);
              if (rootFolder) {
                navigate(`/workflows?teamFolder=${rootFolder.id}`);
              }
            }}
          />
        </>
      )}
    </aside>
  );
}
